import React from 'react';
import { pickerOptions } from '../../utils/pickerOptions';
import { Input as FormInput, Select as FormSelect } from '../FormField';
import TagMultiSelect from '../ui/TagMultiSelect';
import SearchableSelect from '../ui/SearchableSelect';
import { Vehicle, VehicleType, VehicleStatus, Driver } from '../../types/ui/Logistics';
import { VEHICLE_TYPES, VEHICLE_TYPE_LABELS } from './constants';
import '../../styles/components/FormPage.css';

const VEHICLE_FEATURE_SUGGESTIONS = ['AC', 'WiFi', 'Leather seats', 'Bluetooth', 'Chauffeur', 'Decorated'];

export const VEHICLE_STATUS_OPTIONS: Array<{ value: VehicleStatus; label: string }> = [
  { value: 'available', label: 'Available' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in-transit', label: 'In Transit' },
  { value: 'maintenance', label: 'Maintenance' },
];

export type VehicleFormData = Omit<Vehicle, 'id' | 'archived' | 'created_at'>;

interface VehicleFormFieldsProps {
  form: VehicleFormData;
  onChange: (form: VehicleFormData) => void;
  drivers?: Driver[];
  /** Server (422) message for an API field, shown under that field. */
  errorFor?: (field: string) => string | undefined;
}

export const VehicleFormFields: React.FC<VehicleFormFieldsProps> = ({ form, onChange, drivers, errorFor }) => {
  const set = (patch: Partial<VehicleFormData>) => onChange({ ...form, ...patch });

  return (
    <>
      <div className="form-row">
        <FormInput
          label="Vehicle Name"
          type="text"
          placeholder="e.g. White Mercedes Van"
          value={form.name}
          error={errorFor?.('name')}
          onChange={e => set({ name: e.target.value })}
          required
        />
        <FormSelect
          label="Type"
          value={form.type}
          error={errorFor?.('type')}
          onChange={e => set({ type: e.target.value as VehicleType })}
          options={VEHICLE_TYPES.map(t => ({ value: t, label: VEHICLE_TYPE_LABELS[t] }))}
        />
      </div>
      <div className="form-row">
        <FormInput
          label="License Plate"
          type="text"
          placeholder="ABC-123-XY"
          value={form.license_plate}
          error={errorFor?.('license_plate')}
          onChange={e => set({ license_plate: e.target.value })}
          required
        />
        <FormInput
          label="Color"
          type="text"
          placeholder="White, Black..."
          value={form.color ?? ''}
          error={errorFor?.('color')}
          onChange={e => set({ color: e.target.value })}
        />
      </div>
      <div className="form-row">
        <FormInput
          label="Capacity (seats)"
          type="number"
          min={1}
          max={60}
          value={form.capacity}
          error={errorFor?.('capacity')}
          onChange={e => set({ capacity: Number(e.target.value) })}
        />
        {drivers && (
          <div className="form-group">
            <label className="form-label">Assigned Driver</label>
            <SearchableSelect
              value={form.driver_id ?? ''}
              error={errorFor?.('driver_id')}
              onChange={value => set({ driver_id: value })}
              options={[
                { value: '', label: '— No driver assigned —' },
                ...pickerOptions(drivers, form.driver_id, d => `${d.name} (${d.phone})`),
              ]}
            />
          </div>
        )}
      </div>
      <div className="form-section vfp-vehicle-features">
        <p className="form-section-title">Features</p>
        <TagMultiSelect
          values={form.features}
          onChange={features => set({ features })}
          options={VEHICLE_FEATURE_SUGGESTIONS}
          placeholder="AC, WiFi, Leather seats..."
          allowCreate
        />
      </div>
    </>
  );
};
